import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  ChevronDown,
  Gift,
  Loader2,
  ReceiptText,
  ScanLine,
} from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatCurrency, formatRelative } from '@/lib/utils'
import { cameraScanSupported } from '@/lib/scannerCamera'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { useEdition } from '@/hooks/useEdition'
import { useGiftCardTenderEnabled } from '@/hooks/useGiftCardTenderEnabled'
import {
  CameraCardScanner,
  ScanError,
  describeScanError,
} from '@/components/counter/loyaltyCardScanner'
import type { Order, OrderItem, ResolvedLoyaltyCard } from '@/types'
import { describeApiError } from '@/lib/apiError'

/**
 * What the guest actually ordered: voided lines are struck from the bill and
 * draft lines were never fired, so neither helps staff recognise an order.
 *
 * This deliberately does NOT share the counter rail's predicate (F-FE-N154), and
 * the comment that used to say "same filter as SentItemsSection" was the seed of
 * that bug — two filters described as one, free to drift. They answer different
 * questions. The rail lists what the guest is being CHARGED for, so it must show
 * unfired lines. This answers "which order is this?" for a staff member holding
 * a phone, where an unfired line is noise. Exported for tests.
 */
export function visibleOrderItems(order: Order): OrderItem[] {
  return (order.items ?? []).filter((i) => i.status !== 'voided' && i.status !== 'draft')
}

/**
 * If attaching this card would silently replace a DIFFERENT guest already on
 * the order, return that guest's label so the confirm screen can warn and the
 * button can demand a deliberate "Replace". Null means a plain attach: no
 * guest on the order, or the card belongs to the same person (phone match —
 * loyalty identity is phone-keyed; compared digits-only since order phones
 * can arrive formatted from the till). An order with a name but no phone
 * can't be matched, so it warns — replacing is the risky path. Exported for
 * tests.
 */
export function replacedGuestLabel(order: Order, card: ResolvedLoyaltyCard): string | null {
  const existing = (order.customer_name || order.customer_phone || '').trim()
  if (!existing) return null
  const digits = (s: string) => s.replace(/\D/g, '')
  const orderPhone = digits(order.customer_phone ?? '')
  const cardPhone = digits(card.customer_phone ?? '')
  if (orderPhone && cardPhone && orderPhone === cardPhone) return null
  return existing
}

type Step =
  | { name: 'pick' }
  | { name: 'scan'; order: Order }
  | { name: 'confirm'; order: Order; card: ResolvedLoyaltyCard }
  | { name: 'done'; order: Order; card: ResolvedLoyaltyCard }
  // Record-visit mode (marketing edition sensor; also available on full POS):
  | { name: 'vscan' }
  | { name: 'vconfirm'; card: ResolvedLoyaltyCard; token: string }
  | { name: 'vdone'; card: ResolvedLoyaltyCard; punches: number; threshold: number; eligible: boolean; rewardLabel: string }
  // Offer-redeem mode (campaign codes, order-free):
  | { name: 'rcode' }
  | { name: 'rconfirm'; preview: OfferPreview; code: string }
  | { name: 'rdone'; preview: OfferPreview; discountApplied: number }
  // Gift-card mode (ledger-only; staff take off what the guest spent):
  | { name: 'gcode' }
  | { name: 'gconfirm'; preview: GiftPreview }
  | { name: 'gdone'; preview: GiftPreview; redeemed: number; balance: number }

type GiftPreview = {
  id: string
  code: string
  balance: number
  customer_name: string
  redeemable: boolean
  blocker: string
}

type OfferPreview = {
  code: string
  description: string
  discount_type: string
  discount_value: number
  guest_name: string
  guest_phone: string
}

type VisitOutcome = {
  punches: number
  threshold: number
  eligible: boolean
  rewardLabel: string
}

/**
 * Dashboard-stream events that can change the open-orders picker: a new order
 * appears (created), leaves (completed/cancelled/paid/voided), or changes in
 * a way worth repainting (updated — covers guest attaches from other devices).
 * Table/online events deliberately excluded: they can't alter this list.
 * Exported for tests.
 */
export function isScanListEvent(type: string): boolean {
  return (
    type === 'order_created' ||
    type === 'order_updated' ||
    type === 'order_completed' ||
    type === 'order_cancelled' ||
    type === 'order_voided' ||
    type === 'payment'
  )
}

/**
 * The staff-phone scanning flow: pick an open order → scan the guest's wallet
 * QR → confirm the attach. Three screens, phone-sized.
 *
 * ATTACH-ONLY BY DESIGN. This page never writes a punch: it calls the same
 * two endpoints the till uses (resolve-card, then the guest PATCH) and the
 * punch happens inside the payment TX when the bill is closed. UNIQUE(order_id)
 * on loyalty_punches means a phone attach plus a receipt-QR claim on the same
 * order can never double-punch.
 *
 * The order picker is the safety-critical screen — there is no terminal
 * pairing, so choosing the right order IS the pairing. Rows carry order
 * number, type, total, age and any already-attached guest so a wrong-order
 * attach is hard to do silently.
 */
export function StaffScanPage() {
  const queryClient = useQueryClient()
  const { posEnabled, isLoading: editionLoading } = useEdition()
  // F-MM-N24: once a POS tenant can settle bills from a card, this screen
  // stops being a way to SPEND one and becomes a balance lookup. Two ways to
  // spend is how a guest loses a balance and pays in full anyway: staff deduct
  // here, then close the bill at the till for the whole amount. The backend
  // refuses the draw-down in the same case (gift_card_redeem_on_bill_only);
  // this just means staff never see a button that is going to be refused.
  //
  // The marketing edition keeps the old flow — those tenants ring orders on
  // their own till, so there is no bill of ours to attach the card to.
  const { enabled: giftCardTenderEnabled } = useGiftCardTenderEnabled()
  const giftSpendMovedToBill = posEnabled && giftCardTenderEnabled
  const [step, setStep] = useState<Step>({ name: 'pick' })
  // Two modes: 'attach' pairs a card with an open order (full POS — the punch
  // arrives with the payment); 'visit' records the visit right now (the
  // marketing edition's sensor — there is no order to pair with). On
  // marketing deploys attach is meaningless AND its data sources 403, so
  // visit is the only mode there.
  const [modeChoice, setModeChoice] = useState<'attach' | 'visit' | 'redeem' | 'gift' | null>(
    null,
  )

  // Per-venue gate. The admin toggle lives in loyalty settings; this thin
  // probe is counter-accessible. While loading, show the spinner rather than
  // flashing the disabled screen at every open.
  const flagQuery = useQuery({
    queryKey: ['phone-scan-enabled'],
    queryFn: () => apiClient.getPhoneScanEnabled(),
    staleTime: 60_000,
  })

  // The "staff phone scanning" venue toggle gates the POS ATTACH flow
  // (scan-into-an-open-order) only. Record-visit and redeem stand on the
  // loyalty programme itself — on the marketing edition they ARE the
  // product's sensor, and blocking them behind a POS-era toggle left a
  // fresh tenant's core feature dark. When attach isn't available the page
  // lands on record-visit instead of a dead-end "scanning is off" screen.
  const attachAllowed = posEnabled && flagQuery.data?.data?.enabled === true
  const fallbackMode = attachAllowed ? 'attach' : 'visit'
  const mode: 'attach' | 'visit' | 'redeem' | 'gift' =
    modeChoice === 'attach' && !attachAllowed ? 'visit' : (modeChoice ?? fallbackMode)

  // Realtime: same SSE stream the till consumes (counter role is allowed on
  // it). A rung-up order appears here within ~1s of the till creating it. The
  // hook reads onEvent through a ref, so closing over `step` here is fine.
  const streamStatus = useDashboardStream({
    // POS-gated SSE — a marketing-edition deploy 403s it, and visit mode
    // doesn't need order freshness anyway.
    enabled: posEnabled && mode === 'attach',
    onEvent: (ev: DashboardStreamEvent) => {
      if (isScanListEvent(ev.type)) {
        queryClient.invalidateQueries({ queryKey: ['staffScanOpenOrders'] })
      }
      // The order on the scan/confirm screen just got paid on the till —
      // attaching is now pointless (the punch path already ran without this
      // guest). Bounce back to the picker instead of failing on attach.
      if (
        (ev.type === 'payment' || ev.type === 'order_completed') &&
        (step.name === 'scan' || step.name === 'confirm') &&
        ev.order_id === step.order.id
      ) {
        toastHelpers.error(
          'Order was just paid',
          `#${step.order.order_number} closed on the till — the guest can scan the receipt QR instead.`,
        )
        setStep({ name: 'pick' })
      }
    },
  })

  // Open (unpaid) orders only — the natural encoding of "scan before payment".
  // SSE above is the primary freshness signal; the poll is the safety net for
  // a silently dead stream (60s while live, 15s otherwise — the till's idiom).
  const ordersQuery = useQuery({
    queryKey: ['staffScanOpenOrders'],
    queryFn: async () => {
      const r = await apiClient.getOrders({ per_page: 100, open_only: true })
      const list = Array.isArray(r.data) ? r.data : []
      // Newest first — the order being rung up right now is the common case.
      return [...list].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
    },
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
    enabled: posEnabled && mode === 'attach' && flagQuery.data?.data?.enabled === true,
  })

  if (flagQuery.isLoading || editionLoading) {
    return (
      <Shell>
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    )
  }

  const modeTab = (
    m: 'attach' | 'visit' | 'redeem' | 'gift',
    label: string,
    firstStep: Step,
  ) => (
    <button
      key={m}
      type="button"
      className={`rounded-md px-2 py-1.5 transition-colors ${mode === m ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
      onClick={() => {
        setModeChoice(m)
        setStep(firstStep)
      }}
    >
      {label}
    </button>
  )
  // Four modes on a phone screen, so the switcher wraps to two rows rather
  // than squeezing four labels onto one line and truncating every one of them.
  const modeSwitcher = (
    <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 text-sm font-medium">
      {attachAllowed && modeTab('attach', 'Attach', { name: 'pick' })}
      {modeTab('visit', 'Record visit', { name: 'vscan' })}
      {modeTab('redeem', 'Redeem offer', { name: 'rcode' })}
      {modeTab('gift', 'Gift card', { name: 'gcode' })}
    </div>
  )

  if (mode === 'visit' && (step.name === 'pick' || step.name === 'vscan')) {
    return (
      <Shell title="Record a visit">
        {modeSwitcher}
        <VisitScanStep
          onResolved={(card, token) => setStep({ name: 'vconfirm', card, token })}
        />
      </Shell>
    )
  }

  if (step.name === 'vconfirm') {
    return (
      <Shell title="Record visit" onBack={() => setStep({ name: 'vscan' })}>
        <VisitConfirmStep
          card={step.card}
          token={step.token}
          onRecorded={(outcome) =>
            setStep({
              name: 'vdone',
              card: step.card,
              punches: outcome.punches,
              threshold: outcome.threshold,
              eligible: outcome.eligible,
              rewardLabel: outcome.rewardLabel,
            })
          }
        />
      </Shell>
    )
  }

  if (step.name === 'vdone') {
    return (
      <Shell>
        <VisitDoneStep step={step} onAgain={() => setStep({ name: 'vscan' })} />
      </Shell>
    )
  }

  if (mode === 'redeem' && (step.name === 'pick' || step.name === 'rcode')) {
    return (
      <Shell title="Redeem an offer">
        {modeSwitcher}
        <RedeemCodeStep
          onPreview={(preview, code) => setStep({ name: 'rconfirm', preview, code })}
        />
      </Shell>
    )
  }

  if (step.name === 'rconfirm') {
    return (
      <Shell title="Confirm redemption" onBack={() => setStep({ name: 'rcode' })}>
        <RedeemConfirmStep
          preview={step.preview}
          code={step.code}
          onRedeemed={(discountApplied) =>
            setStep({ name: 'rdone', preview: step.preview, discountApplied })
          }
        />
      </Shell>
    )
  }

  if (step.name === 'rdone') {
    return (
      <Shell>
        <RedeemDoneStep step={step} onAgain={() => setStep({ name: 'rcode' })} />
      </Shell>
    )
  }

  if (mode === 'gift' && (step.name === 'pick' || step.name === 'gcode')) {
    return (
      <Shell title="Gift card">
        {modeSwitcher}
        <GiftCodeStep onPreview={(preview) => setStep({ name: 'gconfirm', preview })} />
      </Shell>
    )
  }

  if (step.name === 'gconfirm' && giftSpendMovedToBill) {
    return (
      <Shell title="Gift card balance" onBack={() => setStep({ name: 'gcode' })}>
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Balance</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {formatCurrency(step.preview.balance)}
            </div>
            {step.preview.customer_name ? (
              <div className="mt-1 text-sm text-muted-foreground">{step.preview.customer_name}</div>
            ) : null}
            {!step.preview.redeemable && step.preview.blocker ? (
              <p className="mt-2 text-sm font-medium text-destructive">{step.preview.blocker}</p>
            ) : null}
          </div>
          <p className="text-sm leading-snug text-muted-foreground">
            Gift cards are spent on the bill now — ring the order up at the till and choose{' '}
            <span className="font-medium text-foreground">Apply gift card</span> at payment. The
            card&apos;s history then shows what was ordered.
          </p>
          <Button type="button" className="h-11 w-full" onClick={() => setStep({ name: 'gcode' })}>
            Check another card
          </Button>
        </div>
      </Shell>
    )
  }

  if (step.name === 'gconfirm') {
    return (
      <Shell title="Take off the balance" onBack={() => setStep({ name: 'gcode' })}>
        <GiftConfirmStep
          preview={step.preview}
          onRedeemed={(redeemed, balance) =>
            setStep({ name: 'gdone', preview: step.preview, redeemed, balance })
          }
        />
      </Shell>
    )
  }

  if (step.name === 'gdone') {
    return (
      <Shell>
        <GiftDoneStep step={step} onAgain={() => setStep({ name: 'gcode' })} />
      </Shell>
    )
  }

  if (step.name === 'pick') {
    return (
      <Shell title="Attach a loyalty card">
        {modeSwitcher}
        <OrderPicker
          orders={ordersQuery.data ?? []}
          loading={ordersQuery.isLoading}
          onPick={(order) => setStep({ name: 'scan', order })}
        />
      </Shell>
    )
  }

  if (step.name === 'scan') {
    return (
      <Shell
        title={`Scan for #${step.order.order_number}`}
        onBack={() => setStep({ name: 'pick' })}
      >
        <ScanStep
          order={step.order}
          onResolved={(card) => setStep({ name: 'confirm', order: step.order, card })}
        />
      </Shell>
    )
  }

  if (step.name === 'confirm') {
    return (
      <Shell
        title="Confirm"
        onBack={() => setStep({ name: 'scan', order: step.order })}
      >
        <ConfirmStep
          order={step.order}
          card={step.card}
          onAttached={() => {
            // The till's own open-orders strip refetches on SSE/polling; this
            // refresh keeps THIS phone's picker honest for the next scan.
            queryClient.invalidateQueries({ queryKey: ['staffScanOpenOrders'] })
            setStep({ name: 'done', order: step.order, card: step.card })
          }}
        />
      </Shell>
    )
  }

  if (step.name === 'done') {
    return (
      <Shell>
        <DoneStep
          order={step.order}
          card={step.card}
          onAgain={() => setStep({ name: 'pick' })}
        />
      </Shell>
    )
  }

  // Unreachable in practice (vscan is handled above when mode === 'visit');
  // fall back to the visit scanner rather than a blank screen.
  return (
    <Shell title="Record a visit">
      {modeSwitcher}
      <VisitScanStep onResolved={(card, token) => setStep({ name: 'vconfirm', card, token })} />
    </Shell>
  )
}

/** Page chrome: safe-area padding, centered column, optional back button. */
function Shell({
  title,
  onBack,
  children,
}: {
  title?: string
  onBack?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="min-h-full bg-background pt-[env(safe-area-inset-top,0px)]">
      <div className="mx-auto flex min-h-full w-full max-w-md flex-col">
        {title && (
          <header className="flex items-center gap-2 border-b border-border/70 px-4 py-3">
            {onBack && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 shrink-0 px-0"
                onClick={onBack}
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h1 className="min-w-0 truncate text-base font-semibold">{title}</h1>
          </header>
        )}
        <div className="flex-1 px-4 py-4">{children}</div>
      </div>
    </div>
  )
}

function OrderPicker({
  orders,
  loading,
  onPick,
}: {
  orders: Order[]
  loading: boolean
  onPick: (order: Order) => void
}) {
  // Tap a card to open its item list; the scan action lives inside the
  // expanded area. One card open at a time keeps a 100-order list scannable.
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="space-y-3 py-12 text-center">
        <ReceiptText className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="text-base font-semibold">No open orders</h2>
        <p className="mx-auto max-w-xs text-sm text-muted-foreground">
          Ring the order up first — if it's already paid, the guest can scan the receipt QR
          instead. Don't see the order? Finish creating it on the till.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Pick the guest's order, then scan their wallet card. The visit is added when the order is
        paid.
      </p>
      <ul className="space-y-1.5">
        {orders.map((o) => {
          const items = visibleOrderItems(o)
          const expanded = expandedId === o.id
          return (
            <li
              key={o.id}
              className="rounded-lg border border-border/80 bg-background shadow-sm"
            >
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : o.id)}
                aria-expanded={expanded}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/40 active:bg-muted/60"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm font-semibold">#{o.order_number}</span>
                    <span className="text-xs capitalize text-muted-foreground">
                      {o.order_type.replace(/_/g, ' ')}
                    </span>
                    <span className="ml-auto text-sm font-semibold tabular-nums">
                      {formatCurrency(o.total_amount)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {items.length
                        ? `${items.length} item${items.length === 1 ? '' : 's'} · `
                        : ''}
                      {formatRelative(o.created_at)}
                    </span>
                    {(o.customer_name || o.customer_phone) && (
                      <span className="truncate rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                        {o.customer_name || o.customer_phone}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expanded && (
                <div className="space-y-2 border-t border-border/60 px-3 pb-3 pt-2">
                  {items.length > 0 && (
                    <ul className="space-y-1">
                      {items.map((item) => (
                        <li key={item.id} className="flex items-baseline gap-2 text-sm">
                          <span className="min-w-0 flex-1 truncate">
                            <span className="tabular-nums text-muted-foreground">
                              {item.quantity} ×{' '}
                            </span>
                            {item.product?.name ?? 'Item'}
                            {item.special_instructions && (
                              <span className="block truncate text-xs text-muted-foreground">
                                {item.special_instructions}
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {formatCurrency(item.total_price)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button type="button" className="w-full" onClick={() => onPick(o)}>
                    <ScanLine className="mr-2 h-4 w-4" />
                    Scan loyalty card
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ScanStep({
  order,
  onResolved,
}: {
  order: Order
  onResolved: (card: ResolvedLoyaltyCard) => void
}) {
  // Camera opens immediately — on a phone this page exists to point the
  // camera at a card. The typed-code path stays for USB wedges and dead
  // cameras (same dual input as the till).
  const [camera, setCamera] = useState(() => cameraScanSupported())
  const [code, setCode] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const resolveMutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const token = rawToken.trim()
      if (!token) throw new Error('empty')
      const res = await apiClient.resolveLoyaltyCard(token)
      const card = res.data
      if (!card || !card.enabled) {
        throw new ScanError('loyalty_off', 'Loyalty is off — enable it in admin settings.')
      }
      if (!card.customer_phone) {
        throw new ScanError('no_phone', 'This card has no phone on file to attach.')
      }
      return card
    },
    onSuccess: (card) => {
      setCode('')
      onResolved(card)
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
      setCode('')
      // Re-arm the camera for another try (a decode closes it).
      if (cameraScanSupported()) setCamera(true)
    },
  })

  function submit() {
    if (resolveMutation.isPending || !code.trim()) return
    resolveMutation.mutate(code)
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-sm">
        <span className="font-mono font-semibold">#{order.order_number}</span>
        <span className="text-muted-foreground">
          {' '}
          · {formatCurrency(order.total_amount)}
          {order.items?.length
            ? ` · ${order.items.length} item${order.items.length === 1 ? '' : 's'}`
            : ''}
        </span>
      </div>

      {camera && (
        <CameraCardScanner
          onDecode={(token) => {
            setCamera(false)
            resolveMutation.mutate(token)
          }}
          onClose={() => setCamera(false)}
          busy={resolveMutation.isPending}
        />
      )}

      <div className="flex items-center gap-1.5">
        <Input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          className="h-10 font-mono text-sm"
          placeholder="Or enter card code"
          autoComplete="off"
          spellCheck={false}
          disabled={resolveMutation.isPending}
        />
        <Button
          type="button"
          className="h-10 shrink-0"
          onClick={submit}
          disabled={resolveMutation.isPending || !code.trim()}
        >
          {resolveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>

      {!camera && cameraScanSupported() && (
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => setCamera(true)}
          disabled={resolveMutation.isPending}
        >
          <Camera className="h-4 w-4" />
          Open camera
        </Button>
      )}

      <p className="text-xs leading-snug text-muted-foreground">
        Point the camera at the guest's Apple/Google Wallet loyalty card.
      </p>
    </div>
  )
}

function ConfirmStep({
  order,
  card,
  onAttached,
}: {
  order: Order
  card: ResolvedLoyaltyCard
  onAttached: () => void
}) {
  const replacing = replacedGuestLabel(order, card)
  const attachMutation = useMutation({
    mutationFn: async () => {
      // Same guest endpoint the till uses; the punch stays with the payment
      // path. If the order got paid between pick and confirm it is no longer
      // open server-side and the attach fails cleanly below.
      const res = await apiClient.updateCounterOrderGuest(order.id, {
        customer_phone: card.customer_phone as string,
        customer_name: card.customer_name || undefined,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not attach the customer')
      }
      return res.data
    },
    onSuccess: onAttached,
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
    },
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Order
        </div>
        <div className="text-sm">
          <span className="font-mono font-semibold">#{order.order_number}</span>
          <span className="text-muted-foreground">
            {' '}
            · {formatCurrency(order.total_amount)}
            {order.items?.length
              ? ` · ${order.items.length} item${order.items.length === 1 ? '' : 's'}`
              : ''}
          </span>
        </div>
      </div>

      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Guest
        </div>
        <div className="text-sm font-semibold">{card.customer_name || 'Loyalty member'}</div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Gift className="h-4 w-4" />
          <span className="tabular-nums">
            {card.punches}/{card.threshold}
          </span>
          <span>visits{card.reward_label ? ` toward ${card.reward_label}` : ''}</span>
        </div>
      </div>

      {replacing && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This order is already linked to <span className="font-semibold">{replacing}</span> —
            attaching will replace them, and the visit at payment goes to the new guest.
          </span>
        </div>
      )}

      <Button
        type="button"
        variant={replacing ? 'destructive' : 'default'}
        className="w-full gap-1.5"
        onClick={() => attachMutation.mutate()}
        disabled={attachMutation.isPending}
      >
        {attachMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        {replacing ? `Replace guest on #${order.order_number}` : `Attach to #${order.order_number}`}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        The visit will be added when the order is paid.
      </p>
    </div>
  )
}

function DoneStep({
  order,
  card,
  onAgain,
}: {
  order: Order
  card: ResolvedLoyaltyCard
  onAgain: () => void
}) {
  // Re-arm quickly during a rush: the success screen self-advances back to the
  // picker, but stays long enough to read.
  useEffect(() => {
    const t = setTimeout(onAgain, 8000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4 py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          {card.customer_name || 'Guest'} attached to #{order.order_number}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {card.punches}/{card.threshold} visits
          {card.reward_label ? ` toward ${card.reward_label}` : ''} — the visit will be added when
          the order is paid.
        </p>
      </div>
      <Button type="button" variant="outline" className="gap-1.5" onClick={onAgain}>
        <ScanLine className="h-4 w-4" />
        Scan another
      </Button>
    </div>
  )
}

/**
 * Record-visit scan: same camera + typed-code dual input as the attach flow,
 * minus the order banner — there is no order. Resolution is the same
 * resolve-card lookup; the punch is written by the record-visit endpoint on
 * the NEXT screen, so a mis-scan costs nothing.
 */
function VisitScanStep({
  onResolved,
}: {
  onResolved: (card: ResolvedLoyaltyCard, token: string) => void
}) {
  const [camera, setCamera] = useState(() => cameraScanSupported())
  const [code, setCode] = useState('')

  const resolveMutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const token = rawToken.trim()
      if (!token) throw new Error('empty')
      const res = await apiClient.resolveLoyaltyCard(token)
      const card = res.data
      if (!card || !card.enabled) {
        throw new ScanError('loyalty_off', 'Loyalty is off — enable it in admin settings.')
      }
      return { card, token }
    },
    onSuccess: ({ card, token }) => {
      setCode('')
      onResolved(card, token)
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
      setCode('')
      if (cameraScanSupported()) setCamera(true)
    },
  })

  function submit() {
    if (resolveMutation.isPending || !code.trim()) return
    resolveMutation.mutate(code)
  }

  return (
    <div className="space-y-3">
      {camera && (
        <CameraCardScanner
          onDecode={(token) => {
            setCamera(false)
            resolveMutation.mutate(token)
          }}
          onClose={() => setCamera(false)}
          busy={resolveMutation.isPending}
        />
      )}

      <div className="flex items-center gap-1.5">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          className="h-10 font-mono text-sm"
          placeholder="Or enter card code"
          autoComplete="off"
          spellCheck={false}
          disabled={resolveMutation.isPending}
        />
        <Button
          type="button"
          className="h-10 shrink-0"
          onClick={submit}
          disabled={resolveMutation.isPending || !code.trim()}
        >
          {resolveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>

      {!camera && cameraScanSupported() && (
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => setCamera(true)}
          disabled={resolveMutation.isPending}
        >
          <Camera className="h-4 w-4" />
          Open camera
        </Button>
      )}

      <p className="text-xs leading-snug text-muted-foreground">
        Scan the guest's wallet card to stamp their visit — no order needed.
      </p>
    </div>
  )
}

/**
 * Confirm the visit: guest + balance card, then an OPTIONAL bill amount.
 * The amount is what turns "regulars" into "big spenders" in segments, but
 * stamping never blocks on it — skipping must stay one tap so a busy counter
 * doesn't stop using the flow.
 */
function VisitConfirmStep({
  card,
  token,
  onRecorded,
}: {
  card: ResolvedLoyaltyCard
  token: string
  onRecorded: (outcome: VisitOutcome) => void
}) {
  const [amount, setAmount] = useState('')

  const recordMutation = useMutation({
    mutationFn: async () => {
      const trimmed = amount.trim()
      const parsed = trimmed === '' ? undefined : Number(trimmed)
      if (parsed !== undefined && (!Number.isFinite(parsed) || parsed < 0)) {
        throw new ScanError('bad_amount', 'Enter the bill total as a plain number, or leave it blank.')
      }
      const res = await apiClient.recordLoyaltyVisit({
        card_token: token,
        amount: parsed,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not record the visit')
      }
      return res.data
    },
    onSuccess: (data) => {
      onRecorded({
        punches: data.punches ?? 0,
        threshold: data.threshold ?? 0,
        eligible: data.eligible ?? false,
        rewardLabel: data.reward_label ?? '',
      })
    },
    onError: (err: unknown) => {
      const { code, message } = describeApiError(err)
      if (code === 'visit_cooldown') {
        toastHelpers.error('Already recorded', message)
        return
      }
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
    },
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Guest
        </div>
        <div className="text-sm font-semibold">{card.customer_name || 'Loyalty member'}</div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Gift className="h-4 w-4" />
          <span className="tabular-nums">
            {card.punches}/{card.threshold}
          </span>
          <span>visits{card.reward_label ? ` toward ${card.reward_label}` : ''}</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="visit-amount" className="text-sm font-medium">
          Bill total <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <Input
          id="visit-amount"
          type="number"
          inputMode="decimal"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 1450"
          className="h-11 text-base"
          disabled={recordMutation.isPending}
        />
        <p className="text-xs text-muted-foreground">
          From their receipt. It powers spend insights — skip it if the counter is busy.
        </p>
      </div>

      <Button
        type="button"
        className="w-full gap-1.5"
        onClick={() => recordMutation.mutate()}
        disabled={recordMutation.isPending}
      >
        {recordMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        Record visit{amount.trim() ? ` · ${amount.trim()}` : ''}
      </Button>
    </div>
  )
}

/**
 * Offer-redeem entry: type or scan the code from the guest's WhatsApp
 * message. Preview is read-only — a mis-typed code costs nothing.
 */
function RedeemCodeStep({
  onPreview,
}: {
  onPreview: (preview: OfferPreview, code: string) => void
}) {
  const [code, setCode] = useState('')

  const previewMutation = useMutation({
    mutationFn: async (raw: string) => {
      const res = await apiClient.getOfferPreview(raw.trim().toUpperCase())
      if (!res.success || !res.data) throw new Error(res.message || 'Lookup failed')
      return res.data
    },
    onSuccess: (preview) => {
      onPreview(preview, code.trim().toUpperCase())
      setCode('')
    },
    onError: (err: unknown) => {
      toastHelpers.error('Code not accepted', describeApiError(err).message)
    },
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.trim()) {
              e.preventDefault()
              previewMutation.mutate(code)
            }
          }}
          className="h-11 font-mono text-base tracking-wider"
          placeholder="OFFER CODE"
          autoComplete="off"
          spellCheck={false}
          disabled={previewMutation.isPending}
        />
        <Button
          type="button"
          className="h-11 shrink-0"
          onClick={() => previewMutation.mutate(code)}
          disabled={previewMutation.isPending || !code.trim()}
        >
          {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">
        The code is in the guest's campaign message. Looking it up doesn't
        redeem anything yet.
      </p>
    </div>
  )
}

function RedeemConfirmStep({
  preview,
  code,
  onRedeemed,
}: {
  preview: OfferPreview
  code: string
  onRedeemed: (discountApplied: number) => void
}) {
  const [amount, setAmount] = useState('')

  const redeemMutation = useMutation({
    mutationFn: async () => {
      const trimmed = amount.trim()
      const parsed = trimmed === '' ? undefined : Number(trimmed)
      if (parsed !== undefined && (!Number.isFinite(parsed) || parsed < 0)) {
        throw new ScanError('bad_amount', 'Enter the bill total as a plain number, or leave it blank.')
      }
      const res = await apiClient.redeemOffer({ code, amount: parsed })
      if (!res.success || !res.data) throw new Error(res.message || 'Redeem failed')
      return res.data
    },
    onSuccess: (data) => onRedeemed(data.discount_applied),
    onError: (err: unknown) => {
      const { code, message } = describeApiError(err)
      toastHelpers.error(
        code === 'promo_already_redeemed' ? 'Already used' : 'Could not redeem',
        message,
      )
    },
  })

  const discountLabel =
    preview.discount_type === 'percent'
      ? `${preview.discount_value}% off`
      : `${formatCurrency(preview.discount_value)} off`

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Offer
        </div>
        <div className="font-mono text-sm font-semibold">{preview.code}</div>
        <div className="text-sm">{discountLabel}</div>
        {preview.guest_name && (
          <div className="text-sm text-muted-foreground">
            For {preview.guest_name}
            {preview.guest_phone ? ` · ${preview.guest_phone}` : ''} — check it's
            them before redeeming.
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="redeem-amount" className="text-sm font-medium">
          Bill total <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <Input
          id="redeem-amount"
          type="number"
          inputMode="decimal"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 1450"
          className="h-11 text-base"
          disabled={redeemMutation.isPending}
        />
      </div>

      <Button
        type="button"
        className="w-full gap-1.5"
        onClick={() => redeemMutation.mutate()}
        disabled={redeemMutation.isPending}
      >
        {redeemMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        Redeem {preview.code}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Redeeming marks the code used and adds a visit to the guest's card.
      </p>
    </div>
  )
}

function RedeemDoneStep({
  step,
  onAgain,
}: {
  step: Extract<Step, { name: 'rdone' }>
  onAgain: () => void
}) {
  useEffect(() => {
    const t = setTimeout(onAgain, 8000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4 py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          {step.preview.code} redeemed
          {step.preview.guest_name ? ` for ${step.preview.guest_name}` : ''}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Apply {formatCurrency(step.discountApplied)} off their bill. A visit
          was added to their card.
        </p>
      </div>
      <Button type="button" variant="outline" className="gap-1.5" onClick={onAgain}>
        <ScanLine className="h-4 w-4" />
        Redeem another
      </Button>
    </div>
  )
}

function VisitDoneStep({
  step,
  onAgain,
}: {
  step: Extract<Step, { name: 'vdone' }>
  onAgain: () => void
}) {
  useEffect(() => {
    const t = setTimeout(onAgain, 8000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4 py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          Visit recorded for {step.card.customer_name || 'guest'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {step.punches}/{step.threshold} visits
          {step.rewardLabel ? ` toward ${step.rewardLabel}` : ''}
          {step.eligible ? ' — reward ready! 🎉' : ''}
        </p>
      </div>
      <Button type="button" variant="outline" className="gap-1.5" onClick={onAgain}>
        <ScanLine className="h-4 w-4" />
        Scan next guest
      </Button>
    </div>
  )
}

/**
 * Gift-card mode. Ledger-only: the guest already paid for this value, so
 * staff aren't taking a payment here — they're recording how much of the
 * voucher got used. Look-up is deliberately separate from the debit, so the
 * amount is typed against a balance staff can see.
 */
function GiftCodeStep({ onPreview }: { onPreview: (preview: GiftPreview) => void }) {
  const [code, setCode] = useState('')

  const previewMutation = useMutation({
    mutationFn: async (raw: string) => {
      const res = await apiClient.previewGiftCard(raw.trim())
      if (!res.success || !res.data) throw new Error(res.message || 'Lookup failed')
      return res.data
    },
    onSuccess: (preview) => {
      onPreview(preview)
      setCode('')
    },
    onError: (err: unknown) => {
      toastHelpers.error('Card not found', describeApiError(err).message)
    },
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.trim()) {
              e.preventDefault()
              previewMutation.mutate(code)
            }
          }}
          className="h-11 font-mono text-base tracking-wider"
          placeholder="GC-XXXXXXXX"
          autoComplete="off"
          spellCheck={false}
          disabled={previewMutation.isPending}
        />
        <Button
          type="button"
          className="h-11 shrink-0"
          onClick={() => previewMutation.mutate(code)}
          disabled={previewMutation.isPending || !code.trim()}
        >
          {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">
        You can leave out the GC- and type it in any case. Looking it up takes
        nothing off the card.
      </p>
    </div>
  )
}

function GiftConfirmStep({
  preview,
  onRedeemed,
}: {
  preview: GiftPreview
  onRedeemed: (redeemed: number, balance: number) => void
}) {
  const [amount, setAmount] = useState('')

  const parsed = Number(amount.trim())
  const amountValid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0
  const overBalance = amountValid && parsed > preview.balance

  const redeemMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.redeemGiftCard({ code: preview.code, amount: parsed })
      if (!res.success || !res.data) throw new Error(res.message || 'Redeem failed')
      return res.data
    },
    onSuccess: (d) => onRedeemed(d.redeemed, d.balance),
    onError: (err: unknown) => {
      toastHelpers.error('Not redeemed', describeApiError(err).message)
    },
  })

  if (!preview.redeemable) {
    return (
      <div className="space-y-4 py-8 text-center">
        <p className="text-lg font-semibold">{preview.code}</p>
        <p className="text-sm text-muted-foreground">{preview.blocker}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/40 p-4 text-center">
        <p className="font-mono text-sm text-muted-foreground">{preview.code}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums">
          {formatCurrency(preview.balance)}
        </p>
        <p className="text-xs text-muted-foreground">
          available{preview.customer_name ? ` · ${preview.customer_name}` : ''}
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="gc-take">How much of it did they spend?</Label>
        <Input
          id="gc-take"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && amountValid && !overBalance) {
              e.preventDefault()
              redeemMutation.mutate()
            }
          }}
          className="h-12 text-center text-xl tabular-nums"
          placeholder="0"
          autoFocus
          disabled={redeemMutation.isPending}
        />
        {overBalance ? (
          <p className="text-xs text-destructive">
            That's more than the {formatCurrency(preview.balance)} left on this
            card. Take the rest as a normal payment.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Enter the part of the bill covered by this card — not the whole
            bill, unless the card covers all of it.
          </p>
        )}
      </div>

      <Button
        type="button"
        className="h-11 w-full"
        onClick={() => redeemMutation.mutate()}
        disabled={!amountValid || overBalance || redeemMutation.isPending}
      >
        {redeemMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          `Take off ${amountValid ? formatCurrency(parsed) : ''}`.trim()
        )}
      </Button>
    </div>
  )
}

function GiftDoneStep({
  step,
  onAgain,
}: {
  step: Extract<Step, { name: 'gdone' }>
  onAgain: () => void
}) {
  useEffect(() => {
    const t = setTimeout(onAgain, 8000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4 py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          {formatCurrency(step.redeemed)} taken off
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {step.balance > 0
            ? `${formatCurrency(step.balance)} still on ${step.preview.code}.`
            : `${step.preview.code} is now used up.`}
          {step.preview.customer_name ? ' A visit was added to their card.' : ''}
        </p>
      </div>
      <Button type="button" variant="outline" className="gap-1.5" onClick={onAgain}>
        <ScanLine className="h-4 w-4" />
        Another card
      </Button>
    </div>
  )
}
